/** @type {WeakMap<HTMLElement, HTMLElement[]>} */
const groupMap = new WeakMap();
/** @type {WeakMap<HTMLElement, {header: HTMLElement, content: HTMLElement}>} */
const components = new WeakMap();

export function initAccordions({
    accordionSelector = '.accordion',
    headerSelector = '.accordion-header',
    contentSelector = '.accordion-content',
    openClass = 'accordion-open'
} = {}) {
    /** @type {NodeListOf<HTMLElement>} */
    const accordions = document.querySelectorAll(accordionSelector);

    [...accordions].forEach((accordion, idx) => {

        if (groupMap.has(accordion)) {
            return;
        }

        const id = `accordion-${idx}`;
        const headerId = `${id}-header`;
        const contentId = `${id}-content`;

        /** @type {HTMLElement} */
        const header = accordion.querySelector(headerSelector);
        /** @type {HTMLElement} */
        const content = accordion.querySelector(contentSelector);

        if (!header || !content) {
            return;
        }

        accordion.dataset.accordionOpenClass = openClass;

        components.set(accordion, {header, content});

        /** @type {?HTMLElement} */
        const prevElement = accordion.previousElementSibling;

        if (prevElement?.matches(accordionSelector)) {
            const group = getGroup(prevElement);
            group.push(accordion);
            groupMap.set(accordion, group);
            header.tabIndex = -1;
        } else {
            const group = [];
            group.push(accordion);
            groupMap.set(accordion, group);
            header.tabIndex = 0;
        }

        header.id = headerId;
        header.setAttribute('aria-controls', contentId);
        header.setAttribute('role', 'button');

        content.id = contentId;
        content.setAttribute('aria-labelledby', headerId);
        content.setAttribute('role', 'region');

        if (accordion.classList.contains(openClass)) {
            open(accordion, true);
        } else {
            close(accordion);
        }

        header.addEventListener('click', () => {
            const isOpen = accordion.dataset.open;
            if (isOpen === 'true') {
                close(accordion, true);
            } else {
                open(accordion, true);
            }
        });

        header.addEventListener('keydown', e => {
            const group = getGroup(accordion);

            if (
                group.length > 1
                && (
                    e.key === 'ArrowUp'
                    || e.key === 'ArrowDown'
                    || e.key === 'Home'
                    || e.key === 'End'
                )
            ) {
                e.preventDefault();
            } else {
                return;
            }

            const idx = group.findIndex(acc => acc === accordion);
            const prevElement = group[idx - 1];
            const nextElement = group[idx + 1];
            const firstElement = group[0];
            const lastElement = group[group.length - 1];
            let targetElement;

            if (e.key === 'ArrowDown' && nextElement) {
                targetElement = nextElement;
            }

            if (e.key === 'ArrowUp' && prevElement) {
                targetElement = prevElement;
            }

            if (e.key === 'Home' && firstElement) {
                targetElement = firstElement;
            }

            if (e.key === 'End' && lastElement) {
                targetElement = lastElement;
            }

            if (targetElement) {
                const {header: targetHeader} = getComponents(targetElement);
                targetHeader.tabIndex = 0;
                targetHeader.focus();
                header.tabIndex = -1;
            }
        });

        accordion.dispatchEvent(new CustomEvent('accordion.initialized'));
    });
}

/**
 * closes the passed accordion
 * @param {HTMLElement} accordion
 * @param {boolean} explicit
 */
export function close(accordion, explicit = false) {
    const {header, content} = getComponents(accordion);

    accordion.dispatchEvent(new CustomEvent('accordion.close.before'));

    requestAnimationFrame(() => {
        header.setAttribute('aria-expanded', 'false');

        content.setAttribute('aria-hidden', 'true');
        content.style.height = '0px';

        accordion.dataset.open = 'false';
        accordion.classList.remove(accordion.dataset.accordionOpenClass);

        accordion.dispatchEvent(new CustomEvent('accordion.close', {
            detail: {
                explicit
            }
        }));
    });
}

/**
 * opens the passed accordion
 * @param {HTMLElement} accordion
 * @param {boolean} closeGroupSiblings
 */
export function open(accordion, closeGroupSiblings = false) {
    const {header, content} = getComponents(accordion);

    accordion.dispatchEvent(new CustomEvent('accordion.open.before'));

    requestAnimationFrame(() => {
        header.setAttribute('aria-expanded', 'true');

        content.setAttribute('aria-hidden', 'false');
        content.style.height = `${content.scrollHeight}px`;

        accordion.dataset.open = 'true';
        accordion.classList.add(accordion.dataset.accordionOpenClass);

        accordion.dispatchEvent(new CustomEvent('accordion.open', {
            detail: {
                height: content.scrollHeight
            }
        }));

        if (closeGroupSiblings) {
            const group = getGroup(accordion);
            group.forEach(acc => {
                if (acc !== accordion) {
                    close(acc);
                }
            });
        }
    });
}

/**
 * returns the header and content elements from the passed accordion
 * @param {HTMLElement} accordion
 * @return {{header: HTMLElement, content: HTMLElement}}
 */
export function getComponents(accordion) {
    return components.get(accordion);
}

/**
 * returns the accordion group of the passed accordion
 * @param {HTMLElement} accordion
 * @return {HTMLElement[]}
 */
export function getGroup(accordion) {
    return groupMap.get(accordion);
}